'use client';

import { type ChangeEvent, useEffect, useState } from 'react';
import { Button, Drawer, Heading } from '@petsmart-ui/sparky';
import { usePersonalizedProduct } from '@app/pdp/components/PersonalizedProduct/hooks';
import { useProductDetails } from '@app/pdp/hooks/useProductDetails';
import { SelectedPersonalizationKey, getColorName } from '@libs/algolia';
import { useAddToCTCart } from '@app/pdp/components/AddToCart/hooks/useAddToCTCart';
import { DeliveryType, getCountry, capitalize } from '@libs/core';
import PersonalizedProductSection from './components/PersonalizedProductSection';
import { parseCustomizationLabelAndValues } from './utils/parseCustomizationLabelAndValues';
import type {
    PersonalizeFormInputsDictionaryItem,
    PersonalizedProductProps,
    PersonalizedProduct as PersonalizedProductType,
} from './types';
import './PersonalizedProduct.scss';

export default function PersonalizedProduct({
    quantity,
    disableAddToCart,
}: PersonalizedProductProps) {
    // istanbul ignore next
    const {
        personalizationInputs,
        personalizationFields: { templateId, customizationType },
    } = usePersonalizedProduct();
    const country = getCountry();

    const { addToCTCart } = useAddToCTCart();

    const { selectedProduct, selectedVariants } = useProductDetails();

    const [areFieldsValid, setAreFieldsValid] = useState(true);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false);
    const [inputs, setInputs] = useState<
        PersonalizedProductType['personalizationInputs']
    >(personalizationInputs);
    // istanbul ignore

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    useEffect(() => {
        if (selectedProduct.id) {
            setInputs(personalizationInputs);
            setAreFieldsValid(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- we want to reset state when selected variant changes
    }, [selectedProduct.id]);

    const closeDrawer = () => {
        setInputs(personalizationInputs);
        setIsDrawerOpen(false);
    };

    const openDrawer = () => {
        setIsDrawerOpen(true);
    };

    const updateInput = (
        inputKey: string,
        index: number,
        value: Partial<PersonalizeFormInputsDictionaryItem>
    ) => {
        setInputs(prev => {
            const newInputs = { ...prev };

            newInputs[inputKey][index] = {
                ...newInputs[inputKey][index],
                ...value,
            };

            return newInputs;
        });
    };

    const validateRequiredInput = (inputKey: string, index: number) => {
        const input = inputs[inputKey][index];
        const spaceRegex = /\s/g;
        const inputValue = String(input.value).replaceAll(spaceRegex, '');
        const isInputFilled = input.value !== undefined && inputValue !== '';

        if (input.required && !isInputFilled) {
            updateInput(inputKey, index, {
                isError: true,
                fieldNote: 'Required',
            });

            return false;
        }

        updateInput(inputKey, index, {
            isError: false,
            fieldNote: undefined,
        });

        return true;
    };

    const validateFields = () => {
        let validFields = true;

        Object.keys(inputs).forEach(inputKey => {
            inputs[inputKey].forEach((_, index) => {
                const isRequiredAndFilled = validateRequiredInput(
                    inputKey,
                    index
                );

                if (!isRequiredAndFilled) {
                    validFields = false;
                }
            });
        });

        if (validFields !== areFieldsValid) {
            setAreFieldsValid(validFields);
        }

        return validFields;
    };

    const onInputChange = (
        event: ChangeEvent<HTMLInputElement>,
        index: number,
        inputKey: keyof typeof inputs
    ) => {
        if (hasBeenSubmitted) {
            validateFields();
        }
        setInputs(prev => {
            const newInputs = { ...prev };

            newInputs[inputKey][index].value = event.target.value;

            return newInputs;
        });
    };

    const onAddToCart = async () => {
        const areTheFieldsValid = validateFields();
        setHasBeenSubmitted(true);

        if (!areTheFieldsValid) {
            return;
        }

        const customizationFontName =
            selectedVariants[SelectedPersonalizationKey.FONT_FACE] ?? '';
        const customizationStyle =
            getColorName(
                selectedProduct.personalization?.styleOptions?.threadColors,
                selectedVariants[SelectedPersonalizationKey.THREAD_COLORS]
            ) ?? '';
        const formatedTemplateId = templateId
            ? templateId.toLocaleString('en', {
                  minimumIntegerDigits: 5,
                  useGrouping: false,
              })
            : '0';

        await addToCTCart(
            {
                country,
                sku: selectedProduct.objectID,
                quantity,
                fulfillmentMethod: DeliveryType.STH,
                customizationFontName,
                customizationStyle,
                customizationVasAmount: 0,
                customizationVasType: capitalize(customizationType),
                customizationVasID: formatedTemplateId,
                ...parseCustomizationLabelAndValues(inputs, true),
            },
            true
        );
        closeDrawer();
    };

    const onFrontInputChange = (
        event: ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        onInputChange(event, index, 'front');
    };

    const onBackInputChange = (
        event: ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        onInputChange(event, index, 'back');
    };

    const onInputBlur = () => {
        if (!hasBeenSubmitted) {
            return;
        }

        validateFields();
    };

    return (
        <>
            <Button
                className="personalize-button"
                variant="primary"
                text="Personalize"
                onClick={openDrawer}
                data-testid="personalize-button"
                disabled={disableAddToCart}
            />
            <Drawer
                isActive={isDrawerOpen}
                onClose={closeDrawer}
                align="right"
                ariaLabelledBy="drawer-heading"
                className="personalize-drawer"
                data-testid="personalize-drawer"
            >
                <Drawer.Header>
                    <Heading
                        id="drawer-heading"
                        tagName="h2"
                        size="headline"
                        className="personalize-drawer-header"
                    >
                        Personalize Item
                    </Heading>
                </Drawer.Header>
                <Drawer.Body>
                    {inputs?.front?.length > 0 && (
                        <PersonalizedProductSection
                            title="Front"
                            data-cs-mask
                            text={`You can add up to ${inputs.front.length} lines of
                            text on the front.`}
                            inputs={inputs.front}
                            onInputChange={onFrontInputChange}
                            onBlur={onInputBlur}
                        />
                    )}
                    {inputs?.back?.length > 0 && (
                        <PersonalizedProductSection
                            title="Back"
                            data-cs-mask
                            text={`You can add up to ${inputs.back.length} lines of
                                text on the back.`}
                            inputs={inputs.back}
                            onInputChange={onBackInputChange}
                            onBlur={onInputBlur}
                        />
                    )}
                    <Button
                        className="personalize-button"
                        variant="primary"
                        text="Add to cart"
                        onClick={onAddToCart}
                        fullWidth
                        disabled={!areFieldsValid || disableAddToCart}
                        data-testid="personalize-add-to-cart-button"
                    />
                </Drawer.Body>
            </Drawer>
        </>
    );
}
